import React, { PointerEvent, useEffect, useMemo, useRef, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import Image from '~/components/Common/Image';
import ImageAdjustmentControls from '~/components/Common/Images/ImageAdjustmentControls';
import type { ImageAdjustments } from '~/components/Common/Images/ImageAdjustmentControls';

import type { MediaImage, RoomOption } from '../types';
import { DEFAULT_ROOM_NAME, HERO_ROOM_NAME } from '../utils';

import {
  buildDefaultEdit,
  buildImageFilter,
  calculateCrop,
  getFixedCrop,
  getImageWarmthOverlaySx,
  mergeEditWithDefaults,
} from './ImageEditorUtils';
import type { CropArea, CropHandle, LocalImageEdit } from './ImageEditorUtils';

export type { LocalImageEdit } from './ImageEditorUtils';

interface Props {
  edit?: LocalImageEdit;
  image?: MediaImage;
  open: boolean;
  roomOptions: Array<RoomOption>;
  onClose: () => void;
  onDeleteImage: (image: MediaImage) => void;
  onSave: (publicId: string, edit: LocalImageEdit) => void;
}

interface CropDragState {
  crop: CropArea;
  cropMode: LocalImageEdit['cropMode'];
  handle: CropHandle;
  height: number;
  startX: number;
  startY: number;
  width: number;
}

export default function ImageEditor({ edit, image, open, roomOptions, onClose, onDeleteImage, onSave }: Props) {
  const defaultEdit = useMemo(() => (image ? buildDefaultEdit(image, roomOptions) : undefined), [image, roomOptions]);
  const [draftEdit, setDraftEdit] = useState<LocalImageEdit | undefined>(() =>
    mergeEditWithDefaults(defaultEdit, edit),
  );
  const cropContainerRef = useRef<HTMLDivElement | null>(null);
  const cropDragStateRef = useRef<CropDragState | null>(null);
  const cropDragCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setDraftEdit(mergeEditWithDefaults(defaultEdit, edit));
  }, [defaultEdit, edit]);

  useEffect(() => {
    return () => {
      cropDragCleanupRef.current?.();
      cropDragCleanupRef.current = null;
      cropDragStateRef.current = null;
    };
  }, []);

  if (!image || !draftEdit) {
    return null;
  }

  const roomSelectOptions = [
    { label: HERO_ROOM_NAME, value: HERO_ROOM_NAME },
    ...roomOptions.map((roomOption) => ({
      label: roomOption.roomName,
      value: roomOption.roomId,
    })),
    { label: DEFAULT_ROOM_NAME, value: DEFAULT_ROOM_NAME },
  ];
  const selectedRoomValue = draftEdit.roomId ?? draftEdit.roomName;
  const warmthOverlay = getImageWarmthOverlaySx(draftEdit);
  const adjustments: ImageAdjustments = {
    brightness: draftEdit.brightness,
    contrast: draftEdit.contrast,
    warmth: draftEdit.warmth,
  };

  const handleAdjustmentsChange = (nextAdjustments: ImageAdjustments) => {
    setDraftEdit((currentEdit) => (currentEdit ? { ...currentEdit, ...nextAdjustments } : currentEdit));
  };

  const handleCropModeChange = (nextCropMode: LocalImageEdit['cropMode']) => {
    setDraftEdit((currentEdit) =>
      currentEdit
        ? {
            ...currentEdit,
            crop: nextCropMode === 'fixed' ? getFixedCrop(currentEdit.crop) : currentEdit.crop,
            cropMode: nextCropMode,
          }
        : currentEdit,
    );
  };

  const handleResetAdjustments = () => {
    setDraftEdit((currentEdit) =>
      currentEdit && defaultEdit
        ? {
            ...currentEdit,
            brightness: 0,
            contrast: 0,
            crop: defaultEdit.crop,
            cropMode: defaultEdit.cropMode,
            warmth: 0,
          }
        : currentEdit,
    );
  };

  const updateCrop = (dragState: CropDragState, event: globalThis.PointerEvent) => {
    const deltaX = ((event.clientX - dragState.startX) / dragState.width) * 100;
    const deltaY = ((event.clientY - dragState.startY) / dragState.height) * 100;
    const nextCrop = calculateCrop(dragState.handle, dragState.crop, deltaX, deltaY, dragState.cropMode);

    setDraftEdit((currentEdit) => (currentEdit ? { ...currentEdit, crop: nextCrop } : currentEdit));
  };

  const removeCropDragListeners = () => {
    cropDragCleanupRef.current?.();
    cropDragCleanupRef.current = null;
  };

  const handleStartCropDrag = (handle: CropHandle) => (event: PointerEvent<HTMLElement>) => {
    const container = cropContainerRef.current;

    if (!container) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const containerRect = container.getBoundingClientRect();
    cropDragStateRef.current = {
      crop: draftEdit.crop,
      cropMode: draftEdit.cropMode,
      handle,
      height: containerRect.height,
      startX: event.clientX,
      startY: event.clientY,
      width: containerRect.width,
    };

    const handleCropPointerMove = (pointerEvent: globalThis.PointerEvent) => {
      const dragState = cropDragStateRef.current;

      if (!dragState) {
        return;
      }

      updateCrop(dragState, pointerEvent);
    };
    const handleStopCropDrag = () => {
      cropDragStateRef.current = null;
      removeCropDragListeners();
    };

    removeCropDragListeners();
    cropDragCleanupRef.current = () => {
      window.removeEventListener('pointermove', handleCropPointerMove);
      window.removeEventListener('pointerup', handleStopCropDrag);
    };

    window.addEventListener('pointermove', handleCropPointerMove);
    window.addEventListener('pointerup', handleStopCropDrag);
  };

  const handleSave = () => {
    onSave(image.publicId, draftEdit);
    onClose();
  };

  const handleRoomChange = (nextRoomValue: string) => {
    const matchingRoomOption = roomOptions.find((roomOption) => roomOption.roomId === nextRoomValue);

    setDraftEdit({
      ...draftEdit,
      roomId: matchingRoomOption?.roomId,
      roomName: matchingRoomOption?.roomName ?? nextRoomValue,
    });
  };

  return (
    <Dialog fullWidth maxWidth="xl" open={open} onClose={onClose}>
      <DialogContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2.1fr) minmax(360px, 0.9fr)' },
            gap: 3,
          }}
        >
          <Box
            ref={cropContainerRef}
            sx={{
              position: 'relative',
              alignSelf: 'center',
              borderRadius: 1.5,
              overflow: 'hidden',
              bgcolor: 'grey.900',
            }}
          >
            <Image
              publicId={image.publicId}
              alt={draftEdit.title || image.title || 'Offer image'}
              draggable={false}
              onDragStart={(event) => event.preventDefault()}
              options={{ width: 1120, height: 630 }}
              style={{
                width: '100%',
                aspectRatio: '16 / 9',
                objectFit: 'cover',
                display: 'block',
                filter: buildImageFilter(draftEdit),
              }}
            />
            {warmthOverlay ? <Box sx={warmthOverlay} /> : null}
            <Box sx={getCropFrameSx(draftEdit.crop)} onPointerDown={handleStartCropDrag('move')}>
              {cropHandles.map(({ handle, sx }) => (
                <Box key={handle} sx={{ ...cropHandleSx, ...sx }} onPointerDown={handleStartCropDrag(handle)} />
              ))}
            </Box>
          </Box>

          <Stack spacing={3}>
            <Stack direction="row" justifyContent="flex-end" alignItems="center">
              <Stack direction="row" spacing={1}>
                <IconButton aria-label="Delete image" color="error" onClick={() => onDeleteImage(image)}>
                  <DeleteIcon />
                </IconButton>
                <IconButton aria-label="Close image editor" onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>

            <ImageAdjustmentControls
              adjustmentRange={50}
              adjustments={adjustments}
              cropMode={draftEdit.cropMode}
              fixedAspectRatio={16 / 9}
              onAdjustmentsChange={handleAdjustmentsChange}
              onCropModeChange={handleCropModeChange}
              onReset={handleResetAdjustments}
            />

            <Stack spacing={2}>
              <TextField disabled fullWidth label="Filename" value={draftEdit.filename} />
              <TextField
                fullWidth
                label="Short description"
                value={draftEdit.title}
                onChange={(event) => setDraftEdit({ ...draftEdit, title: event.target.value })}
              />
              <FormControl fullWidth>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Allocate to room
                </Typography>
                <Select value={selectedRoomValue} onChange={(event) => handleRoomChange(event.target.value)}>
                  {roomSelectOptions.map((roomOption) => (
                    <MenuItem key={roomOption.value} value={roomOption.value}>
                      {roomOption.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pt: 0, pb: 2 }}>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave}>
          Save image
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function getCropFrameSx(crop: CropArea) {
  return {
    position: 'absolute',
    top: `${crop.y}%`,
    left: `${crop.x}%`,
    width: `${crop.width}%`,
    height: `${crop.height}%`,
    border: 3,
    borderColor: 'success.main',
    cursor: 'move',
    touchAction: 'none',
  };
}

const cropHandleSx = {
  position: 'absolute',
  width: 18,
  height: 18,
  borderRadius: '50%',
  bgcolor: 'success.main',
  border: 3,
  borderColor: 'common.white',
  touchAction: 'none',
};

const cropHandles: Array<{ handle: CropHandle; sx: object }> = [
  { handle: 'top-left', sx: { top: -8, left: -8, cursor: 'nwse-resize' } },
  { handle: 'top', sx: { top: -8, left: '50%', cursor: 'ns-resize', transform: 'translateX(-50%)' } },
  { handle: 'top-right', sx: { top: -8, right: -8, cursor: 'nesw-resize' } },
  { handle: 'left', sx: { top: '50%', left: -8, cursor: 'ew-resize', transform: 'translateY(-50%)' } },
  { handle: 'right', sx: { top: '50%', right: -8, cursor: 'ew-resize', transform: 'translateY(-50%)' } },
  { handle: 'bottom-left', sx: { bottom: -8, left: -8, cursor: 'nesw-resize' } },
  { handle: 'bottom', sx: { bottom: -8, left: '50%', cursor: 'ns-resize', transform: 'translateX(-50%)' } },
  { handle: 'bottom-right', sx: { right: -8, bottom: -8, cursor: 'nwse-resize' } },
];
